import {
  Box,
  Button,
  Card,
  Grid,
  Heading,
  Input,
  NativeSelect,
  Spinner,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import {
  Field,
  SearchableCombobox,
  type SearchableComboboxOption,
  Toggle,
} from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'
import type { ApiError, ExtensionInstance } from '@mangovoice/types'

import { PickList } from '../../components/PickList'
import {
  useAutoSelectDefaultPlaylist,
  useCreateExtension,
  useGetExtensionInfo,
  useListExtensions,
  usePbxPlaylists,
  usePbxSoundClips,
  useUpdateCallQueue,
  useUpdateExtension,
} from '../../hooks'
import { useExtensionsI18n } from '../../i18n/provider'
import type { CallQueueSeedFields } from '../../types/extension.types'
import {
  CALL_QUEUE_AGENT_RING_STRATEGY_OPTIONS,
  CALL_QUEUE_DESTINATION_TYPES,
  EXTENSION_TYPE_DEFAULT_NUMBER,
  getExtensionTypeLabel,
  getNextExtensionNumber,
  isPlayablePlaylist,
  isReservedExtensionNumber,
  normalizeCallQueueRingStrategy,
  SWITCH_FORM_CSS,
  TOAST_DURATION_ERROR,
  TOAST_DURATION_SUCCESS,
} from '../../utils'
import { getListLabelForSlug, getTypeLabelForSlug } from '../typeSlugLabels'
import { renderWithWrapper } from './renderWithWrapper'
import type { ExtensionEditPageProps } from './types'

export const CallQueueEditPage = ({
  pbxId,
  type,
  extensionId: extensionIdParam,
  toast,
  getListRoute,
  wrapper,
  isUkEnvironment = false,
}: ExtensionEditPageProps) => {
  const { text } = useExtensionsI18n()
  const navigate = useNavigate()
  const pbxIdNum = pbxId
  const isAddMode = extensionIdParam === 'new'
  const extensionId = !isAddMode ? parseInt(extensionIdParam, 10) : undefined
  const isValidId =
    !isAddMode && extensionId != null && !Number.isNaN(extensionId)

  const {
    data: extension,
    isLoading,
    isError,
  } = useGetExtensionInfo({
    extensionId: extensionId!,
    options: { enabled: isValidId },
  })

  const { extensions: lines } = useListExtensions({
    pbxId: pbxIdNum,
    listParams: {
      type: ExtensionType.LINE,
      statusFilter: 'active',
      fetchAll: true,
      limit: 1000,
    },
    options: { enabled: true },
  })
  const { extensions: allExtensions, isLoading: isLoadingAllExtensions } =
    useListExtensions({
      pbxId: pbxIdNum,
      listParams: { limit: 1000, fetchAll: true },
    })
  const { soundClips } = usePbxSoundClips(pbxIdNum)
  const { playlists } = usePbxPlaylists(pbxIdNum)

  // Timeout/escape destinations accept any active extension (A1 parity),
  // excluding this queue itself. Inactive extensions are filtered out so a queue
  // can't route to a disabled destination. Ordered by type (following
  // CALL_QUEUE_DESTINATION_TYPES) then number; the type label is folded into
  // searchText so users can filter by type.
  const destinationOptions = useMemo<SearchableComboboxOption[]>(
    () =>
      CALL_QUEUE_DESTINATION_TYPES.flatMap((type) => {
        const typeLabel = getExtensionTypeLabel(type)
        return allExtensions
          .filter(
            (ext) =>
              ext.type === type &&
              ext.id !== extensionId &&
              ext.active !== false &&
              ext.active !== 0 &&
              ext.is_active !== false &&
              ext.is_active !== 0,
          )
          .sort((a, b) =>
            a.number.localeCompare(b.number, undefined, { numeric: true }),
          )
          .map((ext) => ({
            id: String(ext.id),
            label: `${ext.number} — ${ext.name}`,
            searchText: `${ext.number} ${ext.name} ${typeLabel}`.toLowerCase(),
          }))
      }),
    [allExtensions, extensionId],
  )

  const updateExtensionMutation = useUpdateExtension(pbxIdNum)
  const updateCallQueueMutation = useUpdateCallQueue(pbxIdNum)
  const createExtensionMutation = useCreateExtension(pbxIdNum)

  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [omitFromDirectory, setOmitFromDirectory] = useState(isAddMode)
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [ringStrategy, setRingStrategy] = useState('ring-all')
  const [preCallAnnouncementId, setPreCallAnnouncementId] = useState<string>('')
  const [maxNoAnswer, setMaxNoAnswer] = useState<string>(isAddMode ? '50' : '')
  const [busyDelayTime, setBusyDelayTime] = useState<string>(
    isAddMode ? '10' : '',
  )
  const [rejectDelayTime, setRejectDelayTime] = useState<string>(
    isAddMode ? '10' : '',
  )
  const [wrapUpTime, setWrapUpTime] = useState<string>(isAddMode ? '10' : '')
  const [noAnswerDelayTime, setNoAnswerDelayTime] = useState<string>(
    isAddMode ? '160' : '',
  )
  const [maxRingDuration, setMaxRingDuration] = useState<string>(
    isAddMode ? '8' : '',
  )
  const [queueMaxCallers, setQueueMaxCallers] = useState<string>('50')
  const [callerPlaylistId, setCallerPlaylistId] = useState<string>('')
  const [maxWaitTime, setMaxWaitTime] = useState<string>('600')
  const [timeoutDestinationId, setTimeoutDestinationId] = useState<string>('')
  const [escapeDestinationId, setEscapeDestinationId] = useState<string>('')
  const [periodicAnnouncementsEnable, setPeriodicAnnouncementsEnable] =
    useState(false)
  const [positionAnnouncementFrequency, setPositionAnnouncementFrequency] =
    useState<string>('30')
  const [periodicAnnouncementMessageId, setPeriodicAnnouncementMessageId] =
    useState<string>('')
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  // A queue may already point at a destination that isn't in the list — an
  // intentionally-excluded type (Conference Room / Fax) or an extension that
  // was since deleted. Without an option to resolve it, SearchableCombobox
  // renders blank, hiding the current value. Synthesize a labeled "current"
  // option (using the extension's number/name when we can still find it) so the
  // selection stays visible and an accidental interaction can't silently drop it.
  const withCurrentFallback = (
    selectedId: string,
  ): SearchableComboboxOption[] => {
    if (!selectedId || destinationOptions.some((o) => o.id === selectedId)) {
      return destinationOptions
    }
    const ext = allExtensions.find((e) => String(e.id) === selectedId)
    const label = ext
      ? `${ext.number} — ${ext.name}`
      : text(
          'extensions.edit.callQueue.destinationCurrentUnlisted',
          '{{value}} (current)',
          { value: selectedId },
        )
    return [
      { id: selectedId, label, searchText: label.toLowerCase() },
      ...destinationOptions,
    ]
  }
  const timeoutDestinationOptions = withCurrentFallback(timeoutDestinationId)
  const escapeDestinationOptions = withCurrentFallback(escapeDestinationId)

  useEffect(() => {
    if (!isAddMode || isLoadingAllExtensions) return
    setNumber((prev) => {
      if (prev !== '') return prev
      const sameTypeNumbers = allExtensions
        .filter((ext) => ext.type === ExtensionType.CALL_QUEUE)
        .map((ext) => ext.number)
      const allNumbers = allExtensions.map((ext) => ext.number)
      return getNextExtensionNumber(
        sameTypeNumbers,
        allNumbers,
        EXTENSION_TYPE_DEFAULT_NUMBER['call-queue'] ?? 300,
        isUkEnvironment,
      )
    })
  }, [isAddMode, isLoadingAllExtensions, allExtensions, isUkEnvironment])

  useEffect(() => {
    if (isAddMode) {
      setName(
        (prev) => prev || text('extensions.types.callQueue', 'Call Queue'),
      )
      return
    }
    if (extension && extension.type === ExtensionType.CALL_QUEUE) {
      const cq = extension as ExtensionInstance & CallQueueSeedFields
      setName(cq.name ?? '')
      setNumber(cq.number ?? '')
      setOmitFromDirectory(cq.omit_from_directory ?? false)
      setMemberIds(cq.members?.map((member) => member.id) ?? [])
      setRingStrategy(normalizeCallQueueRingStrategy(cq.ring_strategy))
      setPreCallAnnouncementId(
        cq.pre_call_announcement != null
          ? String(cq.pre_call_announcement)
          : '',
      )
      setQueueMaxCallers(
        cq.queue_max_callers != null ? String(cq.queue_max_callers) : '50',
      )
      setCallerPlaylistId(cq.playlist_id != null ? String(cq.playlist_id) : '')
      setMaxWaitTime(
        cq.max_wait_time != null ? String(cq.max_wait_time) : '600',
      )
      setTimeoutDestinationId(
        cq.queue_timeout_destination != null
          ? String(cq.queue_timeout_destination)
          : '',
      )
      setEscapeDestinationId(
        cq.queue_escape_destination != null
          ? String(cq.queue_escape_destination)
          : '',
      )
      setMaxNoAnswer(cq.max_no_answer != null ? String(cq.max_no_answer) : '')
      setBusyDelayTime(
        cq.busy_delay_time != null ? String(cq.busy_delay_time) : '',
      )
      setRejectDelayTime(
        cq.reject_delay_time != null ? String(cq.reject_delay_time) : '',
      )
      setWrapUpTime(cq.wrap_up_time != null ? String(cq.wrap_up_time) : '')
      setNoAnswerDelayTime(
        cq.no_answer_delay_time != null ? String(cq.no_answer_delay_time) : '',
      )
      setMaxRingDuration(
        cq.max_ring_duration != null ? String(cq.max_ring_duration) : '',
      )
      setPeriodicAnnouncementsEnable(cq.periodic_announcements_enable ?? false)
      setPositionAnnouncementFrequency(
        cq.position_announcement_frequency != null
          ? String(cq.position_announcement_frequency)
          : '30',
      )
      setPeriodicAnnouncementMessageId(
        cq.periodic_announcement_message != null
          ? String(cq.periodic_announcement_message)
          : '',
      )
    }
  }, [extension, isAddMode])

  // Always enabled (not gated on isAddMode) to match legacy: when an existing
  // Call Queue has a NULL playlist_id, legacy selected the PBX default on the
  // rendered form so save upgrades the value. Safe now that the "None" option
  // is gone — there's no way for a user to legitimately set state to ''.
  useAutoSelectDefaultPlaylist(playlists, setCallerPlaylistId, true)

  const numOrNull = (raw: string): number | null => {
    const parsed = parseInt(raw.trim(), 10)
    return raw.trim() === '' || Number.isNaN(parsed)
      ? null
      : Math.max(0, parsed)
  }

  const listPath = getListRoute({ pbxId: pbxIdNum, type })
  const typeLabel = getTypeLabelForSlug(type, text)
  const listLabel = getListLabelForSlug(type, text)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAttemptedSubmit(true)
    if (!name.trim() || !number.trim()) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.nameNumberRequired',
          'Name and number are required.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    // Only guard when the playlist was actually changed. A persisted playlist
    // that later became empty still renders as selected (its option is disabled
    // but pre-selected), so enforcing unconditionally would hard-block saves of
    // unrelated fields for a queue that already points at it.
    const cq = extension as
      (ExtensionInstance & CallQueueSeedFields) | undefined
    const loadedPlaylistId =
      cq?.playlist_id != null ? String(cq.playlist_id) : ''
    const playlistChanged = callerPlaylistId !== loadedPlaylistId
    const selectedPlaylist = playlists.find(
      (p) => String(p.id) === callerPlaylistId,
    )
    if (
      playlistChanged &&
      selectedPlaylist &&
      !isPlayablePlaylist(selectedPlaylist)
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.playlistEmptyError',
          'The selected hold music playlist has no audio tracks. Add tracks or choose a different playlist.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (
      isReservedExtensionNumber(number.trim(), { isUkEnvironment }) &&
      (isAddMode || number.trim() !== (extension?.number ?? '').trim())
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.reservedNumber',
          '{{number}} is reserved. Please select a different number.',
          { number: number.trim() },
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }

    try {
      let targetExtensionId: number | string
      if (isAddMode) {
        const created = await createExtensionMutation.mutateAsync({
          type: ExtensionType.CALL_QUEUE,
          name: name.trim(),
          number: number.trim(),
          omit_from_directory: omitFromDirectory,
          call_queue_members: memberIds,
          members: memberIds,
          ring_strategy: ringStrategy,
        } as never)
        targetExtensionId = created.id
      } else {
        if (!extensionId) return
        targetExtensionId = extensionId
      }

      await updateCallQueueMutation.mutateAsync({
        extensionId: targetExtensionId,
        payload: {
          name: name.trim(),
          number: number.trim(),
          omit_from_directory: omitFromDirectory,
          call_queue_members: memberIds,
          ring_strategy: ringStrategy,
          pre_call_announcement: numOrNull(preCallAnnouncementId),
          max_no_answer: numOrNull(maxNoAnswer),
          busy_delay_time: numOrNull(busyDelayTime),
          reject_delay_time: numOrNull(rejectDelayTime),
          wrap_up_time: numOrNull(wrapUpTime),
          no_answer_delay_time: numOrNull(noAnswerDelayTime),
          max_ring_duration: numOrNull(maxRingDuration),
          queue_max_callers: numOrNull(queueMaxCallers),
          playlist_id: numOrNull(callerPlaylistId),
          max_wait_time: numOrNull(maxWaitTime),
          queue_timeout_destination: numOrNull(timeoutDestinationId),
          queue_escape_destination: numOrNull(escapeDestinationId),
          periodic_announcements_enable: periodicAnnouncementsEnable,
          position_announcement_frequency: numOrNull(
            positionAnnouncementFrequency,
          ),
          periodic_announcement_message: numOrNull(
            periodicAnnouncementMessageId,
          ),
        },
      })

      toast.create({
        title: isAddMode
          ? text('extensions.edit.callQueue.created', 'Call queue created')
          : text('extensions.edit.callQueue.updated', 'Call queue updated'),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
      navigate(listPath)
    } catch (error: unknown) {
      const apiErr = error as ApiError | undefined
      toast.create({
        title: isAddMode
          ? text('extensions.edit.createFailed', 'Create failed')
          : text('extensions.edit.updateFailed', 'Update failed'),
        description:
          apiErr?.message ??
          (error as Error)?.message ??
          text(
            'extensions.edit.callQueue.saveFailed',
            'Failed to save call queue',
          ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const isPending =
    updateExtensionMutation.isPending ||
    updateCallQueueMutation.isPending ||
    createExtensionMutation.isPending
  const allLineItems = lines
    .filter((line) => line.type === ExtensionType.LINE)
    .map((line) => ({ id: line.id, label: `${line.number} — ${line.name}` }))
  const breadcrumbs = [
    { label: listLabel, path: listPath },
    {
      label: isAddMode
        ? text('extensions.edit.breadcrumbAdd', 'Add {{typeLabel}}', {
            typeLabel,
          })
        : text(
            'extensions.edit.breadcrumbEdit',
            'Edit {{typeLabel}} {{value}}',
            { typeLabel, value: extension?.number ?? extension?.id ?? '' },
          ),
    },
  ]

  if (!isAddMode && (isLoading || !extension)) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Box display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" />
      </Box>,
    )
  }

  if (
    !isAddMode &&
    (isError || !extension || extension.type !== ExtensionType.CALL_QUEUE)
  ) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Card.Root>
        <Card.Body p={6}>
          <Heading size="md" mb={2}>
            {text('extensions.edit.callQueue.notType', 'Not a call queue')}
          </Heading>
          <Link
            to={listPath}
            style={{ color: 'var(--chakra-colors-blue-500)' }}
          >
            {text('extensions.edit.backToList', '← Back to {{listLabel}}', {
              listLabel,
            })}
          </Link>
        </Card.Body>
      </Card.Root>,
    )
  }

  return renderWithWrapper(
    wrapper,
    breadcrumbs,
    <form onSubmit={handleSubmit}>
      <VStack gap={6} align="stretch" css={SWITCH_FORM_CSS}>
        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>{typeLabel}</Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root required invalid={attemptedSubmit && !name.trim()}>
                <Field.Label>
                  {text('extensions.columns.name', 'Name')}
                </Field.Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root required invalid={attemptedSubmit && !number.trim()}>
                <Field.Label>
                  {text('extensions.columns.number', 'Number')}
                </Field.Label>
                <Input
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root gridColumn="1 / -1">
                <Toggle
                  colorPalette="brand"
                  checked={omitFromDirectory}
                  onCheckedChange={(e) =>
                    setOmitFromDirectory(e.checked === true)
                  }
                  disabled={isPending}
                >
                  {text(
                    'extensions.edit.omitFromDirectory',
                    'Omit from directory',
                  )}
                </Toggle>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text(
                'extensions.edit.callQueue.queueSettings',
                'Queue Settings',
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.ringStrategy',
                    'Ring Strategy',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={ringStrategy}
                    onChange={(e) => setRingStrategy(e.target.value)}
                  >
                    {CALL_QUEUE_AGENT_RING_STRATEGY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value === 'ring-all'
                          ? text(
                              'extensions.edit.callQueue.strategyRingAll',
                              'Ring All',
                            )
                          : option.value === 'round-robin'
                            ? text(
                                'extensions.edit.callQueue.strategyRoundRobin',
                                'Round Robin',
                              )
                            : option.value === 'longest-idle-agent'
                              ? text(
                                  'extensions.edit.callQueue.strategyLongestIdle',
                                  'Longest Idle',
                                )
                              : option.value === 'random'
                                ? text(
                                    'extensions.edit.callQueue.strategyRandom',
                                    'Random',
                                  )
                                : option.value === 'agent-with-least-talk-time'
                                  ? text(
                                      'extensions.edit.callQueue.strategyLeastTalkTime',
                                      'Least Talk Time',
                                    )
                                  : text(
                                      'extensions.edit.callQueue.strategyFewestCalls',
                                      'Fewest Calls',
                                    )}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.preCallAnnouncement',
                    'Pre Call Announcement',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={preCallAnnouncementId}
                    onChange={(e) => setPreCallAnnouncementId(e.target.value)}
                  >
                    <option value="">
                      {text('extensions.common.none', 'None')}
                    </option>
                    {soundClips.map((clip) => (
                      <option key={clip.id} value={clip.id}>
                        {clip.name}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.maxNoAnswer',
                    'Max no answer (attempts)',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={maxNoAnswer}
                  onChange={(e) => setMaxNoAnswer(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.busyDelayTime',
                    'Busy delay time (seconds)',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={busyDelayTime}
                  onChange={(e) => setBusyDelayTime(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.rejectDelayTime',
                    'Reject delay time (seconds)',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={rejectDelayTime}
                  onChange={(e) => setRejectDelayTime(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.wrapUpTime',
                    'Wrap-up time (seconds)',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={wrapUpTime}
                  onChange={(e) => setWrapUpTime(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.noAnswerDelayTime',
                    'No answer delay time (seconds)',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={noAnswerDelayTime}
                  onChange={(e) => setNoAnswerDelayTime(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.maxRingDuration',
                    'Max ring duration (seconds)',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={maxRingDuration}
                  onChange={(e) => setMaxRingDuration(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.maxCallersInQueue',
                    'Max callers in queue',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={queueMaxCallers}
                  onChange={(e) => setQueueMaxCallers(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text('extensions.edit.holdMusic', 'Hold Music')}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={callerPlaylistId}
                    onChange={(e) => setCallerPlaylistId(e.target.value)}
                  >
                    {playlists.map((playlist) => {
                      const playable = isPlayablePlaylist(playlist)
                      return (
                        <option
                          key={playlist.id}
                          value={playlist.id}
                          disabled={!playable}
                        >
                          {playable
                            ? playlist.name
                            : text(
                                'extensions.edit.playlistNotPlayableOption',
                                '{{name}} (no audio tracks)',
                                { name: playlist.name },
                              )}
                        </option>
                      )
                    })}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.maxWaitTime',
                    'Max wait time (seconds)',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={maxWaitTime}
                  onChange={(e) => setMaxWaitTime(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.timeoutDestination',
                    'Timeout destination',
                  )}
                </Field.Label>
                <SearchableCombobox
                  options={timeoutDestinationOptions}
                  value={timeoutDestinationId}
                  onChange={setTimeoutDestinationId}
                  disabled={isPending}
                  noneOptionLabel={text('extensions.common.none', 'None')}
                  placeholder={text(
                    'extensions.edit.callQueue.destinationSearchPlaceholder',
                    'Search extensions...',
                  )}
                  searchAriaLabel={text(
                    'extensions.edit.callQueue.timeoutDestination',
                    'Timeout destination',
                  )}
                  emptyMessage={text(
                    'extensions.edit.callQueue.destinationNoMatches',
                    'No extension matches that search.',
                  )}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.escapeDestination',
                    'Escape destination',
                  )}
                </Field.Label>
                <SearchableCombobox
                  options={escapeDestinationOptions}
                  value={escapeDestinationId}
                  onChange={setEscapeDestinationId}
                  disabled={isPending}
                  noneOptionLabel={text('extensions.common.none', 'None')}
                  placeholder={text(
                    'extensions.edit.callQueue.destinationSearchPlaceholder',
                    'Search extensions...',
                  )}
                  searchAriaLabel={text(
                    'extensions.edit.callQueue.escapeDestination',
                    'Escape destination',
                  )}
                  emptyMessage={text(
                    'extensions.edit.callQueue.destinationNoMatches',
                    'No extension matches that search.',
                  )}
                />
              </Field.Root>
              <Field.Root gridColumn="1 / -1">
                <Toggle
                  colorPalette="brand"
                  checked={periodicAnnouncementsEnable}
                  onCheckedChange={(e) =>
                    setPeriodicAnnouncementsEnable(e.checked === true)
                  }
                >
                  {text(
                    'extensions.edit.callQueue.periodicAnnouncements',
                    'Periodic announcements',
                  )}
                </Toggle>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.positionAnnouncementFrequency',
                    'Position announcement frequency',
                  )}
                </Field.Label>
                <Input
                  type="number"
                  min={0}
                  value={positionAnnouncementFrequency}
                  onChange={(e) =>
                    setPositionAnnouncementFrequency(e.target.value)
                  }
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.callQueue.positionAnnouncementMessage',
                    'Position announcement message',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={periodicAnnouncementMessageId}
                    onChange={(e) =>
                      setPeriodicAnnouncementMessageId(e.target.value)
                    }
                  >
                    <option value="">
                      {text(
                        'extensions.edit.callQueue.queuePosition',
                        'Queue Position',
                      )}
                    </option>
                    {soundClips.map((clip) => (
                      <option key={clip.id} value={clip.id}>
                        {clip.name}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        {!isAddMode && (
          <Card.Root>
            <Card.Header p={6} pb={0}>
              <Card.Title>
                {text('extensions.edit.members', 'Members')}
              </Card.Title>
            </Card.Header>
            <Card.Body p={6} pt={3}>
              <PickList
                allItems={allLineItems}
                assignedIds={memberIds}
                onAssignedChange={setMemberIds}
                assignedLabels={Object.fromEntries(
                  (
                    extension as ExtensionInstance & CallQueueSeedFields
                  )?.members?.map((member) => [
                    member.id,
                    `${member.number} — ${member.name}`,
                  ]) ?? [],
                )}
                itemNoun={text(
                  'extensions.edit.callQueue.agentsNoun',
                  'agents',
                )}
                disabled={isPending}
              />
            </Card.Body>
          </Card.Root>
        )}

        <Box pt={2} px={4} pb={3}>
          <Button
            type="submit"
            colorPalette="brand"
            loading={isPending}
            borderRadius="4px"
          >
            {isAddMode
              ? text('extensions.edit.callQueue.newButton', 'New Call Queue')
              : text(
                  'extensions.edit.callQueue.updateButton',
                  'Update Call Queue',
                )}
          </Button>
          <Button
            type="button"
            variant="outline"
            ml={3}
            borderRadius="4px"
            asChild
          >
            <Link to={listPath}>
              {text('extensions.common.cancel', 'Cancel')}
            </Link>
          </Button>
        </Box>
      </VStack>
    </form>,
  )
}
